import { useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { useContextEngineStore } from '@/stores/contextEngineStore'
import { ConflictResolution } from '@/components/context/ConflictResolution'

export function ConflictsPage() {
  const {
    conflicts,
    conflictsLoading,
    conflictsError,
    fetchConflicts,
    resolveConflict,
  } = useContextEngineStore()

  useEffect(() => {
    fetchConflicts()
  }, [fetchConflicts])

  const unresolvedConflicts = useMemo(
    () => conflicts.filter((c) => c.status === 'unresolved'),
    [conflicts],
  )

  const resolvedConflicts = useMemo(
    () => conflicts.filter((c) => c.status === 'resolved'),
    [conflicts],
  )

  // Loading
  if (conflictsLoading && conflicts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-accent-amber" size={32} />
      </div>
    )
  }

  // Error
  if (conflictsError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center p-4">
        <AlertTriangle size={24} className="text-accent-error" />
        <p className="text-text-secondary">Couldn&apos;t load conflicts.</p>
        <button
          onClick={fetchConflicts}
          className="text-sm text-accent-amber hover:underline"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty — no conflicts
  if (conflicts.length === 0) {
    return (
      <div className="p-6 lg:p-8 max-w-[1000px] mx-auto">
        <h1 className="font-display text-2xl font-bold text-text-primary mb-6">Conflicts</h1>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent-success/10">
            <CheckCircle2 size={32} className="text-accent-success" />
          </div>
          <h2 className="font-display text-lg font-semibold text-text-primary mb-1">
            No conflicts
          </h2>
          <p className="text-sm text-text-secondary max-w-sm">
            Your context is consistent! When contradicting information is detected, it will appear here for resolution.
          </p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1000px] mx-auto space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-text-primary mb-1">Conflicts</h1>
        <p className="text-sm text-text-secondary">
          {unresolvedConflicts.length} unresolved
          {resolvedConflicts.length > 0 && ` · ${resolvedConflicts.length} resolved`}
        </p>
      </div>

      {/* Unresolved conflicts */}
      {unresolvedConflicts.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-display text-base font-semibold text-text-primary flex items-center gap-2">
            <AlertTriangle size={16} className="text-accent-amber" />
            Needs Resolution
          </h2>
          {unresolvedConflicts.map((conflict) => (
            <ConflictResolution
              key={conflict.conflictId}
              conflict={conflict}
              onResolve={resolveConflict}
            />
          ))}
        </section>
      )}

      {/* Resolved conflicts */}
      {resolvedConflicts.length > 0 && (
        <section className="space-y-4">
          <h2 className="font-display text-base font-semibold text-text-tertiary">
            Previously Resolved
          </h2>
          {resolvedConflicts.map((conflict) => (
            <ConflictResolution
              key={conflict.conflictId}
              conflict={conflict}
              onResolve={resolveConflict}
            />
          ))}
        </section>
      )}
    </div>
  )
}
